import React, {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextString from '../../components/CLocalizedTextString';
import CPageModule from '../../components/CPageModule';
import CPageModulePlot from '../../components/CPageModulePlot';
import CStatefulContainer from '../../components/CStatefulContainer';
import CTab from '../../components/CTab';
import CTabItem from '../../components/CTabItem';
import Page from '../../components/layout/Page';
import {updateStateResponseStatefulAttribute, useStateExtended} from '../../helpers/helper';
import { useAppSelector } from '../../hooks/hooks';
import { type IResponse } from '../../models/IResponse';
import { type PlotlyResponse } from '../../models/PlotlyModel';
import PublicFactsheetServices from '../../services/publicFactsheetServices';

interface State {
  deltaCrossSectionalDistribution: IResponse<{ equity_exposure: PlotlyResponse; position_delta: PlotlyResponse }>;
  deltaChangeMatrix: IResponse<{
    risk_utilization: PlotlyResponse;
    average_delta: PlotlyResponse;
    delta_over_time: PlotlyResponse;
  }>;
  deltaBySector: IResponse<{
    risk_utilization: PlotlyResponse;
    average_delta: PlotlyResponse;
    delta_over_time: PlotlyResponse;
  }>;
}

export default function PDelta() {
  const { factsheetId } = useParams();
  const { locale } = useAppSelector((state) => state.dashboard);

  const [state, setState] = useStateExtended<State>({
    deltaCrossSectionalDistribution: { state: { isLoading: true, isError: false } },
    deltaChangeMatrix: { state: { isLoading: true, isError: false } },
    deltaBySector: { state: { isLoading: true, isError: false } }
  });


  useEffect(() => {
    (async () => {
      await Promise.all([
        updateStateResponseStatefulAttribute<State, typeof state.deltaCrossSectionalDistribution.data>(
          { deltaCrossSectionalDistribution: state.deltaCrossSectionalDistribution },
          setState,
          async () =>
            await PublicFactsheetServices.getDeltaCrossSectionalDistribution(factsheetId ?? '')
        ),
        updateStateResponseStatefulAttribute<State, typeof state.deltaChangeMatrix.data>(
          { deltaChangeMatrix: state.deltaChangeMatrix },
          setState,
          async () =>
            await PublicFactsheetServices.getDeltaChangeMatrixGrouped(factsheetId ?? '')
        ),
        updateStateResponseStatefulAttribute<State, typeof state.deltaBySector.data>(
          { deltaBySector: state.deltaBySector },
          setState,
          async () =>
            await PublicFactsheetServices.getDeltaBySectorGrouped(factsheetId ?? '')
        ),
      ]);
    })();
  }, [factsheetId, locale]);


  return (
    <Page title={CLocalizedTextString('globalFactsheetSectionDelta')}>
      <CPageModule>
        <h2>
          <CLocalizedText dictKey={'fsriskP1H1'} />
        </h2>
        <CLocalizedText dictKey={'fsriskP1Desc'} />
      </CPageModule>
      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CPageModule submodule>
          <h3>
            <CLocalizedText dictKey={'fsriskP2H1'} />
          </h3>
          <CLocalizedText dictKey={'fsriskP2Desc'} />
        </CPageModule>
        <CTab submodule>
          <CTabItem label={'Equity Exposure'} text={CLocalizedTextString('factsheet.delta.tabs.equityExposure')}>
            <CStatefulContainer
              submodule
              apiRequestState={state.deltaCrossSectionalDistribution?.state}
              showContent={!!state.deltaCrossSectionalDistribution?.data}
            >
              <CPageModulePlot
                data={state.deltaCrossSectionalDistribution.data?.equity_exposure?.data}
                layout={state.deltaCrossSectionalDistribution.data?.equity_exposure?.layout}
              />
            </CStatefulContainer>
          </CTabItem>
          <CTabItem label={'Position Delta'} text={CLocalizedTextString('factsheet.delta.tabs.positionDelta')}>
            <CStatefulContainer
              submodule
              apiRequestState={state.deltaCrossSectionalDistribution?.state}
              showContent={!!state.deltaCrossSectionalDistribution?.data}
            >
              <CPageModulePlot
                data={state.deltaCrossSectionalDistribution.data?.position_delta?.data}
                layout={state.deltaCrossSectionalDistribution.data?.position_delta?.layout}
              />
            </CStatefulContainer>
          </CTabItem>
        </CTab>
      </CPageModule>

      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CPageModule submodule>
          <CPageModule submodule fullWidth style={{ paddingTop: 0 }}>
            <h3>
              <CLocalizedText dictKey={'factsheet.delta.module2.title'}/>
            </h3>
          </CPageModule>
          <CLocalizedText dictKey={'fsriskP4P2Desc'} />
        </CPageModule>
        <CTab submodule>
          <CTabItem label={'riskUtilization'} text={CLocalizedTextString('factsheet.delta.tabs.riskUtilization')}>
            <CStatefulContainer
              submodule
              apiRequestState={state.deltaBySector?.state}
              showContent={!!state.deltaBySector?.data}
            >
              <CPageModulePlot
                data={state.deltaBySector.data?.risk_utilization?.data}
                layout={state.deltaBySector.data?.risk_utilization?.layout}
              />
            </CStatefulContainer>
          </CTabItem>

          <CTabItem label={'averageDelta'} text={CLocalizedTextString('factsheet.delta.tabs.averageDelta')}>
            <CStatefulContainer
              submodule
              apiRequestState={state.deltaBySector?.state}
              showContent={!!state.deltaBySector?.data}
            >
              <CPageModulePlot
                data={state.deltaBySector.data?.average_delta?.data}
                layout={state.deltaBySector.data?.average_delta?.layout}
              />
            </CStatefulContainer>
          </CTabItem>

          <CTabItem label={'deltaOverTime'} text={CLocalizedTextString('factsheet.delta.tabs.deltaOverTime')}>
            <CStatefulContainer
              submodule
              apiRequestState={state.deltaBySector?.state}
              showContent={!!state.deltaBySector?.data}
            >
              <CPageModulePlot
                data={state.deltaBySector.data?.delta_over_time?.data}
                layout={state.deltaBySector.data?.delta_over_time?.layout}
              />
            </CStatefulContainer>
          </CTabItem>
        </CTab>
      </CPageModule>

      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CPageModule submodule>
          <h3>
            <CLocalizedText dictKey={'factsheet.delta.module3.title'}/>
          </h3>
          <CLocalizedText dictKey={'fsriskP4P1Desc'} />
        </CPageModule>
        <CTab submodule>
          <CTabItem label={'riskUtilization'} text={CLocalizedTextString('factsheet.delta.tabs.riskUtilization')}>
            <CStatefulContainer
              submodule
              apiRequestState={state.deltaChangeMatrix?.state}
              showContent={!!state.deltaChangeMatrix?.data}
            >
              <CPageModulePlot
                data={state.deltaChangeMatrix.data?.risk_utilization?.data}
                layout={state.deltaChangeMatrix.data?.risk_utilization?.layout}
              />
            </CStatefulContainer>
          </CTabItem>

          <CTabItem label={'averageDelta'} text={CLocalizedTextString('factsheet.delta.tabs.averageDelta')}>
            <CStatefulContainer
              submodule
              apiRequestState={state.deltaChangeMatrix?.state}
              showContent={!!state.deltaChangeMatrix?.data}
            >
              <CPageModulePlot
                data={state.deltaChangeMatrix.data?.average_delta?.data}
                layout={state.deltaChangeMatrix.data?.average_delta?.layout}
              />
            </CStatefulContainer>
          </CTabItem>

          <CTabItem label={'deltaOverTime'} text={CLocalizedTextString('factsheet.delta.tabs.deltaOverTime')}>
            <CStatefulContainer
              submodule
              apiRequestState={state.deltaChangeMatrix?.state}
              showContent={!!state.deltaChangeMatrix?.data}
            >
              <CPageModulePlot
                data={state.deltaChangeMatrix?.data?.delta_over_time.data}
                layout={state.deltaChangeMatrix?.data?.delta_over_time.layout}
              />
            </CStatefulContainer>
          </CTabItem>
        </CTab>
      </CPageModule>
    </Page>
  );
}